/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import {
    Autocomplete,
    type AutocompleteRootHighlightEventDetails,
} from '@base-ui/react/autocomplete';
import { Dialog } from '@base-ui/react/dialog';
import * as React from 'react';

import { Icon, Spinner } from 'sarsaparilla';

import { useAuthState } from 'shared-ui/auth';
import { UIcon } from 'shared-ui/uicon';

import {
    findSuggestionGroup,
    getGroupEntryAnnouncement,
    shouldAnnounceHighlights,
} from './QuickSearch.announcements';
import { navigateToSuggestion, startsNewSearch } from './QuickSearch.navigation';
import {
    useClearSearchHistoryMutation,
    useSearchHistoryQuery,
    useSearchSuggestionsQuery,
} from './QuickSearch.queries';
import { createEmptyStateGroups, prependQuerySuggestion } from './QuickSearch.transforms';
import type { SearchSuggestionGroup, SearchSuggestionItem } from './QuickSearch.types';
import { SuggestionList } from './SuggestionList';

import styles from './QuickSearch.module.scss';

/** How long an announcement stays in the live region, so it can't be found later by browsing. */
const ANNOUNCEMENT_TIMEOUT_MS = 7000;

export function QuickSearch() {
    const [isOpen, setIsOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState<string>('');
    const [highlightedItem, setHighlightedItem] = React.useState<
        SearchSuggestionItem | undefined
    >(undefined);
    const [announcement, setAnnouncement] = React.useState<string | undefined>(undefined);
    // The platform can't change while mounted, so it's checked once rather than per highlight
    const [announcesHighlights] = React.useState(shouldAnnounceHighlights);
    const lastHighlightedGroupType = React.useRef<
        SearchSuggestionGroup['groupType'] | undefined
    >(undefined);

    const shortcutsDescriptionId = React.useId();

    const { account } = useAuthState();
    const { data, isFetching, isError } = useSearchSuggestionsQuery(inputValue.trim());
    const { data: history } = useSearchHistoryQuery({
        accountId: account?.account_id,
        enabled: isOpen,
    });
    const clearHistory = useClearSearchHistoryMutation();

    React.useEffect(() => {
        function handleKeyDown(event: KeyboardEvent) {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                // Also keeps the browser from focusing its own search bar on Ctrl+K
                event.preventDefault();
                setIsOpen(true);
            }
        }

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const items = React.useMemo(() => {
        if (!inputValue.trim()) {
            return createEmptyStateGroups(history);
        }

        return prependQuerySuggestion(data ?? [], inputValue);
    }, [data, history, inputValue]);

    React.useEffect(() => {
        if (!announcement) {
            return undefined;
        }

        const timeout = window.setTimeout(
            () => setAnnouncement(undefined),
            ANNOUNCEMENT_TIMEOUT_MS
        );
        return () => window.clearTimeout(timeout);
    }, [announcement]);

    /**
     * Tracks the highlighted row for the footer hint and, on Apple platforms, announces
     * the group when arrow keys move into a new one. Auto-highlights while typing stay
     * silent so they don't talk over the user's typing.
     */
    function handleItemHighlighted(
        item: SearchSuggestionItem | undefined,
        { reason }: AutocompleteRootHighlightEventDetails
    ) {
        setHighlightedItem(item);

        if (!announcesHighlights) {
            return;
        }

        const group = item ? findSuggestionGroup(items, item) : undefined;
        const isGroupChange =
            group !== undefined && group.groupType !== lastHighlightedGroupType.current;
        lastHighlightedGroupType.current = group?.groupType;

        if (!item || reason !== 'keyboard') {
            return;
        }

        // VoiceOver skips a message identical to the one already in the live region,
        // so it's cleared whenever there's nothing to say. Re-entering a group then
        // reads as new.
        setAnnouncement(getGroupEntryAnnouncement({ item, group, isGroupChange }));
    }

    /** Forgets the last group and message so the reopened palette starts fresh. */
    function resetAnnouncements() {
        setAnnouncement(undefined);
        lastHighlightedGroupType.current = undefined;
    }

    function handleItemClick(item: SearchSuggestionItem) {
        if (item.type === 'clear-history') {
            clearHistory.mutate();
            return;
        }

        setIsOpen(false);
        setInputValue('');
        resetAnnouncements();
        navigateToSuggestion(item);
    }

    return (
        <Dialog.Root
            open={isOpen}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                    setInputValue('');
                    clearHistory.reset();
                    resetAnnouncements();
                }
                setIsOpen(nextOpen);
            }}
        >
            <Dialog.Trigger
                className={styles.Button}
                aria-label="Search"
                aria-keyshortcuts="Meta+K Control+K"
            >
                <UIcon name="search" size={20} strokeWidth={2.5} />
                Search
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Backdrop className={styles.Backdrop} />
                <Dialog.Viewport className={styles.Viewport}>
                    <Dialog.Popup className={styles.Popup} aria-label="Command palette">
                        <Autocomplete.Root
                            open
                            inline
                            mode="none"
                            items={items}
                            autoHighlight="always"
                            keepHighlight
                            value={inputValue}
                            onValueChange={setInputValue}
                            onItemHighlighted={handleItemHighlighted}
                        >
                            <Autocomplete.InputGroup className={styles.InputGroup}>
                                <UIcon
                                    name="search"
                                    size={18}
                                    className={styles.InputIcon}
                                    aria-hidden
                                />
                                <Autocomplete.Input
                                    className={styles.Input}
                                    aria-label="Search commands"
                                    aria-describedby={shortcutsDescriptionId}
                                    placeholder="Search …"
                                />

                                {isFetching ? (
                                    <div
                                        className={styles.SpinnerContainer}
                                        aria-hidden="true"
                                    >
                                        <Spinner size="xs" />
                                    </div>
                                ) : null}
                            </Autocomplete.InputGroup>
                            <Dialog.Close
                                className={styles.SkipLink}
                                aria-label="Close search"
                            >
                                <Icon iconName="close" size="sm" />
                            </Dialog.Close>

                            <div className={styles.ListArea}>
                                <div className={styles.ListViewport}>
                                    <div className={styles.ListContent}>
                                        <Autocomplete.Empty>
                                            <div className={styles.Empty}>
                                                No results found
                                            </div>
                                        </Autocomplete.Empty>

                                        <SuggestionList onItemClick={handleItemClick} />

                                        <Autocomplete.Status>
                                            {isFetching ? (
                                                <div className="rec-sr-only">
                                                    Loading suggestions
                                                </div>
                                            ) : isError ? (
                                                <div className={styles.errorStatus}>
                                                    <Icon iconName="warning" />
                                                    Sorry, suggestions aren’t loading
                                                    right now
                                                </div>
                                            ) : clearHistory.isSuccess &&
                                              !inputValue.trim() ? (
                                                <div className="rec-sr-only">
                                                    Search history cleared
                                                </div>
                                            ) : null}
                                        </Autocomplete.Status>
                                    </div>
                                </div>
                            </div>

                            <div
                                className="rec-sr-only"
                                role="log"
                                aria-live="assertive"
                                aria-relevant="additions"
                            >
                                {/* VoiceOver only re-announces when a node is added. A
                                    placeholder of a different element type makes React swap
                                    nodes instead of updating text, so a repeated message (e.g.
                                    re-entering a group) is still read. Keep it a div. */}
                                {announcement ? (
                                    <span>{announcement}</span>
                                ) : (
                                    <div>&nbsp;</div>
                                )}
                            </div>

                            <div className={styles.Footer}>
                                <span id={shortcutsDescriptionId} className="rec-sr-only">
                                    Use Enter to activate the highlighted item.
                                </span>
                                <div className={styles.FooterLeft}>
                                    <kbd className={styles.Kbd}>Enter</kbd>
                                    <span>{getEnterHint(highlightedItem)}</span>
                                </div>
                                <div className={styles.FooterRight}>
                                    <kbd className={styles.Kbd}>Esc</kbd>
                                    <span>Close</span>
                                </div>
                            </div>
                        </Autocomplete.Root>
                    </Dialog.Popup>
                </Dialog.Viewport>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

function getEnterHint(item: SearchSuggestionItem | undefined) {
    if (item?.type === 'clear-history') {
        return 'Clear search history';
    }

    return item && startsNewSearch(item) ? 'Start a new map search' : 'Navigate';
}
